import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import qs from "qs";
import saveAs from "file-saver";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface IPcapTrace {
  id: string;
  accountId: string;
  accountName?: string;
  ipAddress: string;
  durationSeconds: number;
  status: PcapTraceStatus;
  startedAt?: string;
  stoppedAt?: string;
  stopAt?: string;
  filePath?: string;
  filename?: string;
  fileSize?: number;
  errorMessage?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  downloadAvailable?: boolean;
}

export enum PcapTraceStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  STOPPED = "STOPPED",
  FAILED = "FAILED",
}

export interface IStartPcapTraceRequest {
  accountId: string;
  ipAddress: string;
  durationSeconds: number;
}

export interface IStartPcapTraceResponse {
  id: string;
  status: PcapTraceStatus;
  message: string;
}

export async function startPcapTrace(
  request: IStartPcapTraceRequest
): Promise<IStartPcapTraceResponse> {
  try {
    const response = await axiosInstance.post("/pcap-traces/start", request);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while starting PCAP trace."
    );
  }
}

export async function stopPcapTrace(traceId: string): Promise<IPcapTrace> {
  try {
    const response = await axiosInstance.post(`/pcap-traces/${traceId}/stop`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while stopping PCAP trace."
    );
  }
}

// Get status of a specific PCAP trace
export async function getPcapTraceStatus(traceId: string): Promise<IPcapTrace> {
  try {
    const response = await axiosInstance.get(`/pcap-traces/${traceId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching PCAP trace status."
    );
  }
}

// function to clean params instead of sending ?param= in URL
function cleanParams<T extends Record<string, any>>(params: T): Partial<T> {
  const cleaned: Partial<T> = {};
  for (const key of Object.keys(params)) {
    const value = params[key];
    // Only include params that have actual non-empty values
    if (value !== "" && value !== null && value !== undefined) {
      cleaned[key as keyof T] = value;
    }
  }
  return cleaned;
}

export async function fetchPcapTraces(
  params: Params<IPcapTrace & { fromDate?: string; toDate?: string }>
): Promise<IPageable<IPcapTrace>> {
  try {
    const finalParams = cleanParams(params);

    const response = await axiosInstance.get("/pcap-traces", {
      params: finalParams,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("The requested resource was not found.");
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching PCAP traces."
    );
  }
}

export async function downloadPcapFile(
  traceId: string,
  filePath?: string
): Promise<void> {
  try {
    const response = await axiosInstance.get(
      `/pcap-traces/${traceId}/download`,
      {
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/octet-stream",
    });
    const finalFilename = filePath || `pcap_trace_${traceId}.pcap`;
    saveAs(blob, finalFilename);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while downloading PCAP file."
    );
  }
}

export async function exportPcapTraces(
  params: Params<IPcapTrace & { fromDate?: string; toDate?: string }> = {}
): Promise<void> {
  try {
    const finalParams = cleanParams(params);

    const response = await axiosInstance.get("/pcap-traces/csv", {
      params: finalParams,
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, "pcap_traces.csv");
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while exporting PCAP traces."
    );
  }
}

export async function getActiveTraceByAccount(
  accountId: string
): Promise<IPcapTrace | null> {
  try {
    const response = await axiosInstance.get(
      `/pcap-traces/active/${accountId}`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while checking active trace."
    );
  }
}

export function usePcapTracesQuery(
  params: Params<IPcapTrace>,
  options?: { enabled?: boolean; refetchInterval?: number }
): UseQueryResult<IPageable<IPcapTrace>, Error> {
  return useQuery({
    queryKey: ["pcapTraces", params],
    queryFn: () => fetchPcapTraces(params),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
    staleTime: 0,
  });
}

export function usePcapTraceStatusQuery(
  traceId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number }
): UseQueryResult<IPcapTrace, Error> {
  return useQuery({
    queryKey: ["pcapTraceStatus", traceId],
    queryFn: () => getPcapTraceStatus(traceId!),
    enabled: !!traceId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    staleTime: 0,
  });
}
